/**
 * AgreementPreviewDialog — R1 (2026-09-28). Temporary, non-persistent rendering of the ACTUAL agreement via the existing
 * POST /agreements/{id}/documents/preview (same renderer/merge context as Generate; stores nothing, no lifecycle effect).
 * The HTML is shown in a sandboxed iframe: no `allow-scripts`, so template HTML can never execute or reach the parent page;
 * `allow-same-origin` only lets the parent call print() on the frame (browser Print / Save as PDF — no server PDF endpoint).
 */
import React, { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Eye, Printer, AlertTriangle } from 'lucide-react';

const errOf = (e) => e.response?.data?.error || e.response?.data?.detail || {};

export function AgreementPreviewDialog({ open, onOpenChange, agreement, templateId }) {
  const [state, setState] = useState({ loading: false, html: '', template: null, sample: null, error: null });
  const frameRef = useRef(null);

  useEffect(() => {
    if (!open || !agreement?.id) return;
    let cancelled = false;
    setState({ loading: true, html: '', template: null, sample: null, error: null });
    api.post(`/agreements/${agreement.id}/documents/preview`, templateId ? { template_id: templateId } : {})
      .then((r) => { if (!cancelled) { const d = r.data?.data || {}; setState({ loading: false, html: d.html || '', template: d.template || null, sample: d.sample_state || d.template?.sample_state || null, error: null }); } })
      .catch((e) => { if (!cancelled) { const er = errOf(e); setState({ loading: false, html: '', template: null, sample: null, error: { code: er.code || `HTTP_${e.response?.status || ''}`, message: er.message || (typeof er === 'string' ? er : 'Preview could not be rendered') } }); } });
    return () => { cancelled = true; };
  }, [open, agreement?.id, templateId]);

  const print = () => { try { frameRef.current?.contentWindow?.print(); } catch { /* blocked by browser */ } };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] flex flex-col" data-testid="agreement-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Eye className="h-4 w-4" /> Preview Agreement {agreement?.agreement_no}</DialogTitle>
          <DialogDescription data-testid="agreement-preview-disclaimer">
            Preview Agreement — not a frozen/signed document. Nothing is stored and the agreement status does not change. Use <b>Generate Agreement Document</b> to create the frozen document used for sending and signature.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between gap-2 text-xs">
          <div className="flex items-center gap-2 flex-wrap">
            {state.template && <span className="text-muted-foreground" data-testid="agreement-preview-template">Template: <b>{state.template.name}</b> · v{state.template.version}</span>}
            {state.sample && <Badge variant="destructive" data-testid="agreement-preview-sample">SAMPLE — NOT FOR SIGNATURE</Badge>}
            <Badge variant="outline" data-testid="agreement-preview-badge">Preview only · not stored</Badge>
          </div>
          <Button size="sm" variant="outline" onClick={print} disabled={!state.html} data-testid="agreement-preview-print"><Printer className="h-3.5 w-3.5 mr-1" /> Print / Save as PDF</Button>
        </div>
        {state.loading && <p className="text-sm text-muted-foreground py-8 text-center" data-testid="agreement-preview-loading">Rendering preview…</p>}
        {state.error && (
          <div className="rounded border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2 flex gap-2" data-testid="agreement-preview-error">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /><span><b>{state.error.code}</b> — {state.error.message}</span>
          </div>
        )}
        {state.html && (
          <iframe ref={frameRef} title="Agreement preview" sandbox="allow-same-origin allow-modals" srcDoc={state.html}
            className="w-full flex-1 min-h-[60vh] rounded border bg-white" data-testid="agreement-preview-frame" />
        )}
      </DialogContent>
    </Dialog>
  );
}

export default AgreementPreviewDialog;
